import { Box } from '@chakra-ui/react';
import { DateSelectOption } from '@gamma/ui/form-fields';
import MailSharpIcon from '@gamma/ui/icons/MailSharpIcon';
import { SlideDrawer } from '@gamma/ui/layout';
import { Investigator } from '@gamma/ui/logos';
import { ErrorPage, SplitPage } from '@gamma/ui/pages';
import {
  Dispatch,
  FC,
  SetStateAction,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useNavigate,
} from 'react-router';
import React, { lazy } from 'react';
import { ErrorCode, Features, URLS } from '../shared/constants/constants';
import { ROUTES } from '../shared/constants/routes';
import {
  AuthContext,
  DateRangeContext,
  SlideDrawerContext,
} from '../shared/providers';
import { EntitiesProvider } from '../features/entities/context';
import {
  EntityAnalysisModal,
  EntityInformationModal,
  EntitySuppressionModal,
  EntityIsolationModal,
} from '../features/entities/components';
import { useAgenticTriageSettings } from '../features/entities/hooks';
import { ConditionalRedirects } from './ConditionalRedirects';
import { UserExistenceGuard } from './UserExistenceGuard';
import {
  Account,
  ForgotPassword,
  Login,
  Registration,
} from '../features/user/pages';
import { PrivilegedAccessWrapper, Wrapper } from '../shared/components';
import { Onboard } from '../features/system/pages';
import {
  Access,
  SecurityAudit as SecurityAuditImpl,
  SensorMonitoring,
  UsersAccess,
  UsersAndAccess,
} from '../features/admin/pages';
import {
  AlertCatalogDetails,
  AlertCatalogOutlet,
  Detection,
  DetectionDetails,
  LockedAlertExport,
} from '../features/detections/pages';
import { AlertsCatalogTable } from '../features/detections/components';
import {
  HumioDashboardOverview,
  HumioDashboards,
  HumioSearch,
  Overview,
} from '../features/investigations/pages';
import { LoadingSpinner } from '@gamma/ui/progress';
import { SecurityOverview } from '../features/entities/pages';
import { CognitoUserInterface } from '../shared/types/auth-types';

const {
  main: LoginMain,
  trouble: LoginTrouble,
  mfa: LoginMfa,
  twoFactorRecovery: LoginTwoFactorRecovery,
  ssoLogin: LoginSsoLogin,
  ssoLoginErrors: LoginSsoLoginErrors,
} = Login;
const {
  main: RegistrationMain,
  setPassword: RegistrationSetPassword,
  confirmOTP: RegistrationConfirmOTP,
  complete: RegistrationComplete,
  welcome: RegistrationWelcome,
  ssoWelcome: RegistrationSsoWelcome,
} = Registration;
const {
  main: ForgotPasswordMain,
  setUser: ForgotPasswordSetUser,
  resetPassword: ForgotPasswordResetPassword,
  complete: ForgotPasswordComplete,
} = ForgotPassword;

// Lazy load page components using named exports
const Admin = lazy(() =>
  import('../features/admin/pages/Admin').then((m) => ({ default: m.Admin })),
);
const GeneralImpl = lazy(() =>
  import('../features/admin/pages/General').then((m) => ({
    default: m.General,
  })),
);
const System = lazy(() =>
  import('../features/admin/pages/System').then((m) => ({ default: m.System })),
);
const Detections = lazy(() =>
  import('../features/detections/pages/Detections').then((m) => ({
    default: m.Detections,
  })),
);
const AlertToDetection = lazy(() =>
  import('../features/detections/pages/AlertToDetection').then((m) => ({
    default: m.AlertToDetection,
  })),
);
const Integrations = lazy(() =>
  import('../features/integrations/pages/Integrations').then((m) => ({
    default: m.Integrations,
  })),
);
const IntegrationsAlertExports = lazy(() =>
  import('../features/integrations/pages/IntegrationsAlertExports').then(
    (m) => ({ default: m.IntegrationsAlertExports }),
  ),
);
const IntegrationsConfig = lazy(() =>
  import('../features/integrations/pages/IntegrationsConfig').then((m) => ({
    default: m.IntegrationsConfig,
  })),
);
const IntegrationsDetailPage = lazy(() =>
  import('../features/integrations/pages/IntegrationsDetailPage').then((m) => ({
    default: m.IntegrationsDetailPage,
  })),
);

const { home, systemSettingsSensorMonitoring, notFound } = ROUTES;

const guarded = (Component: React.ComponentType<object>) => {
  return function GuardedComponent(props: object) {
    const navigate = useNavigate();
    const { userRole } = useContext(AuthContext);
    useEffect(() => {
      if (userRole !== 'admin') {
        navigate(notFound);
      }
    }, [navigate, userRole]);
    return <Component {...props} />;
  };
};

const General = guarded(GeneralImpl);
const SecurityAudit = guarded(SecurityAuditImpl);

// --- Layouts ---

// Layout that always renders children (public + protected branches share this)
const ProtectedLayout = ({
  isSSOLogin,
  setSearch,
  setDateRange,
  defaultRangeIndex,
  setDefaultRangeIndex,
  setAuthStateLoading,
  setTcAcceptanceLoading,
}: {
  isSSOLogin: boolean;
  setSearch: (s: string) => void;
  setDateRange?: (dateRange?: DateSelectOption | undefined) => void;
  defaultRangeIndex?: number;
  setDefaultRangeIndex?: (v: number) => void;
  setAuthStateLoading: (v: boolean) => void;
  setTcAcceptanceLoading: (v: boolean) => void;
}) => {
  return (
    <>
      <UserExistenceGuard />
      <ConditionalRedirects
        setSearch={setSearch}
        isSSOLogin={isSSOLogin}
        setDateRange={setDateRange}
        defaultRangeIndex={defaultRangeIndex}
        setDefaultRangeIndex={setDefaultRangeIndex}
        setAuthStateLoading={setAuthStateLoading}
        setTcAcceptanceLoading={setTcAcceptanceLoading}
      />
      {/* Always render children here so /login and other public pages show */}
      <Outlet />
    </>
  );
};

// Layout for public pages ex: login
const PublicLayout = () => (
  <SplitPage
    links={[
      {
        Icon: MailSharpIcon,
        name: 'Contact Support',
        url: URLS.CORELIGHT_SUPPORT,
        isExternal: true,
      },
    ]}
    logo={<Investigator />}
    left={<Outlet />}
    right={
      <Box
        w="full"
        h="full"
        bgColor="black"
        bg="url(/assets/images/CL_particle_wipe_black.jpg)"
        bgPos="center"
        bgSize="cover"
      />
    }
  />
);

// Wrapper for protected routes only — gate here by tokenReady
const SlideDrawerWrapper: FC<{
  user?: CognitoUserInterface;
  tcAcceptanceLoading: boolean;
  tokenReady: boolean; // gate protected content on token
}> = ({ user, tokenReady }) => {
  const { agenticTriageFeatureEnabled } = useAgenticTriageSettings();
  const location = { pathname: window.location.pathname };

  const {
    portalType,
    setIsSlideDrawerOpen,
    setPortalType,
    isSlideDrawerOpen,
    slideDrawerOnClose,
    slideDrawerTitle,
    slideDrawerActions,
    slideDrawerBody,
  } = useContext(SlideDrawerContext);

  useEffect(() => {
    setIsSlideDrawerOpen?.(false);
    setPortalType?.(undefined);
  }, [location.pathname, setIsSlideDrawerOpen, setPortalType]);

  return (
    <EntitiesProvider>
      <Wrapper user={user}>
        {/* Only render protected tree once token is available */}
        {tokenReady ? <Outlet /> : null}
        <SlideDrawer
          isOpen={isSlideDrawerOpen}
          hideHeader={portalType === 'alert'}
          title={slideDrawerTitle}
          onClose={() => {
            slideDrawerOnClose?.();
            setIsSlideDrawerOpen?.(false);
          }}
          actions={slideDrawerActions}
        >
          {slideDrawerBody}
        </SlideDrawer>
      </Wrapper>
      {agenticTriageFeatureEnabled ? (
        <EntityAnalysisModal />
      ) : (
        <EntityInformationModal />
      )}
      <EntitySuppressionModal />
      <EntityIsolationModal />
    </EntitiesProvider>
  );
};

// --- AppRoutes ---

export interface AppRoutesProps {
  isAdmin?: boolean;
  isSSOLogin: boolean;
  preferredMFA: string;
  user?: CognitoUserInterface;
  setPreferredMFA: Dispatch<SetStateAction<string>>;
  tokenReady: boolean;
}

export const AppRoutes = ({
  user,
  isAdmin,
  isSSOLogin,
  preferredMFA,
  setPreferredMFA,
  tokenReady,
}: AppRoutesProps) => {
  const { isOrgTenant } = useContext(AuthContext);
  const { setDateRange, defaultRangeIndex, setDefaultRangeIndex } =
    useContext(DateRangeContext);
  const [search, setSearch] = useState('');
  const [, setAuthStateLoading] = useState(true);
  const [tcAcceptanceLoading, setTcAcceptanceLoading] = useState(false);

  const router = useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route
          path="/"
          element={
            <ProtectedLayout
              isSSOLogin={isSSOLogin}
              setSearch={setSearch}
              setDateRange={setDateRange}
              defaultRangeIndex={defaultRangeIndex}
              setDefaultRangeIndex={setDefaultRangeIndex}
              setAuthStateLoading={setAuthStateLoading}
              setTcAcceptanceLoading={setTcAcceptanceLoading}
            />
          }
        >
          {/* Public routes */}
          <Route element={<PublicLayout />}>
            <Route path="sso-error" element={<LoginSsoLoginErrors />} />
            <Route
              path="privacy-policy"
              element={<RegistrationSsoWelcome user={user} />}
            />
            <Route path="registration" element={<RegistrationMain />}>
              <Route index element={<RegistrationWelcome />} />
              <Route path="setpassword" element={<RegistrationSetPassword />} />
              <Route path="mfa" element={<RegistrationConfirmOTP />} />
              <Route
                path="complete"
                element={<RegistrationComplete preferredMFA={preferredMFA} />}
              />
            </Route>
            <Route path="login">
              <Route
                index
                element={<LoginMain setPreferredMFA={setPreferredMFA} />}
              />
              <Route path="mfa" element={<LoginMfa />} />
              <Route path="trouble">
                <Route index element={<LoginTrouble />} />
                <Route path="password" element={<ForgotPasswordMain />}>
                  <Route index element={<ForgotPasswordSetUser />} />
                  <Route
                    path="reset"
                    element={<ForgotPasswordResetPassword />}
                  />
                  <Route path="complete" element={<ForgotPasswordComplete />} />
                </Route>
                <Route
                  path="two-factor-recovery"
                  element={<LoginTwoFactorRecovery />}
                />
              </Route>
              <Route path="sso-login" element={<LoginSsoLogin />} />
            </Route>
          </Route>

          {/* Protected routes (gated by tokenReady) */}
          <Route
            path=""
            element={
              <SlideDrawerWrapper
                user={user}
                tcAcceptanceLoading={tcAcceptanceLoading}
                tokenReady={tokenReady}
              />
            }
          >
            <Route index element={<Onboard isAdmin={isAdmin} />} />
            <Route path="account/settings" element={<Account />} />
            <Route path="admin" element={<Admin />}>
              <Route element={<UsersAndAccess />}>
                <Route path="users" element={<UsersAccess />} />
                <Route path="access" element={<Access />} />
              </Route>
            </Route>
            <Route path="system/settings" element={<System />}>
              <Route
                index
                element={<Navigate to={systemSettingsSensorMonitoring} />}
              />
              <Route path="sensor-monitoring" element={<SensorMonitoring />} />
              <Route path="general" element={<General />} />
              <Route
                path="integrations"
                element={
                  !isOrgTenant ? <Integrations /> : <Navigate to={notFound} />
                }
              >
                <Route path="configuration" element={<IntegrationsConfig />} />
                <Route
                  path=":integration/configuration"
                  element={<IntegrationsDetailPage />}
                />
                <Route
                  path="alert-exports"
                  element={<IntegrationsAlertExports />}
                >
                  <Route
                    path=":exporter_id"
                    element={
                      isAdmin ? (
                        <PrivilegedAccessWrapper
                          feature={Features.alertExport}
                          component={<IntegrationsAlertExports />}
                          lockedComponent={<LockedAlertExport />}
                        />
                      ) : (
                        <Navigate to={notFound} />
                      )
                    }
                  />
                </Route>
              </Route>
              <Route path="alert-catalog" element={<AlertCatalogOutlet />}>
                <Route index element={<AlertsCatalogTable />} />
                <Route path=":content_id" element={<AlertCatalogDetails />} />
              </Route>
              <Route path="audit-logs" element={<SecurityAudit />} />
            </Route>
            <Route path="dashboard" element={<Overview />}>
              <Route index element={<Navigate to={home} />} />
              <Route
                path="security-overview"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <SecurityOverview />
                  </Suspense>
                }
              />
              <Route
                path="threat-hunting"
                element={<HumioDashboardOverview />}
              />
              <Route
                path="network-overview"
                element={<HumioDashboardOverview />}
              />
              <Route
                path="security-posture"
                element={<HumioDashboardOverview />}
              />
              <Route path="more-dashboards" element={<HumioDashboards />} />
            </Route>
            <Route path="log-search" element={<HumioSearch />} />
            <Route path="detections">
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Detections />
                  </Suspense>
                }
              />
              <Route path=":detection_id" element={<Detection />}>
                <Route index element={<Navigate to={`details${search}`} />} />
                <Route path="details" element={<DetectionDetails />} />
              </Route>
            </Route>
            <Route path="alert-to-detection">
              <Route index element={<AlertToDetection />} />
              <Route path=":alert_id" element={<AlertToDetection />} />
            </Route>
            <Route
              path="404"
              element={<ErrorPage code={ErrorCode.NOT_FOUND} />}
            />
            <Route
              path="500"
              element={<ErrorPage code={ErrorCode.SERVER_ERROR} />}
            />
          </Route>

          {/* Global 404 */}
          <Route path="*" element={<Navigate to={notFound} />} />
        </Route>,
      ),
    );
  }, [
    isSSOLogin,
    setDateRange,
    defaultRangeIndex,
    setDefaultRangeIndex,
    user,
    preferredMFA,
    setPreferredMFA,
    tcAcceptanceLoading,
    isAdmin,
    isOrgTenant,
    search,
    tokenReady,
  ]);

  return <RouterProvider router={router} />;
};
